import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Header() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const catRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const k = keyword.trim();
    navigate(k ? `/category/all?q=${encodeURIComponent(k)}` : "/");
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 shadow-sm shadow-primary-100/40 backdrop-blur"
          : "bg-background-50"
      }`}
    >
      <div className="container-page flex h-16 items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2" aria-label="喵趣漫画网首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 text-background-50">
            <i className="ri-emotion-happy-line text-xl" aria-hidden="true"></i>
          </span>
          <span className="font-heading text-xl font-bold text-foreground-950">
            喵趣<span className="text-primary-600">漫画</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex" aria-label="主导航">
          <Link to="/" className="rounded-full px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-secondary-100 hover:text-secondary-900">
            首页
          </Link>
          <div className="relative" ref={catRef}>
            <button
              type="button"
              onClick={() => setCatOpen((v) => !v)}
              className="flex items-center gap-1 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-secondary-100 hover:text-secondary-900 cursor-pointer"
            >
              漫画分类
              <i className={`ri-arrow-down-s-line text-base transition-transform ${catOpen ? "rotate-180" : ""}`} aria-hidden="true"></i>
            </button>
            {catOpen && (
              <div className="absolute left-0 top-full mt-2 w-52 rounded-xl border border-background-200 bg-background-50 p-2 shadow-lg shadow-primary-100/30">
                <Link
                  to="/category/all"
                  className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground-800 transition-colors hover:bg-secondary-100"
                >
                  <i className="ri-grid-fill text-secondary-700" aria-hidden="true"></i>
                  全部作品
                </Link>
                {categories.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/category/${c.slug}`}
                    className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground-800 transition-colors hover:bg-secondary-100"
                  >
                    <i className="ri-hashtag text-secondary-700" aria-hidden="true"></i>
                    {c.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
          <Link to="/about" className="rounded-full px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-secondary-100 hover:text-secondary-900">
            关于我们
          </Link>
          <Link to="/faq" className="rounded-full px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-secondary-100 hover:text-secondary-900">
            常见问题
          </Link>
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <form onSubmit={onSearch} className="relative" role="search">
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜一搜萌系漫画"
              className="w-44 rounded-full border border-background-200 bg-background-100 py-2 pl-4 pr-9 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
            />
            <button type="submit" className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-foreground-500 hover:bg-secondary-100 hover:text-secondary-900 cursor-pointer" aria-label="搜索">
              <i className="ri-search-line text-lg" aria-hidden="true"></i>
            </button>
          </form>
          <Link to="/faq" className="btn-primary">
            开始追更
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="flex h-10 w-10 items-center justify-center rounded-full text-foreground-800 hover:bg-secondary-100 md:hidden cursor-pointer"
          aria-label="打开菜单"
        >
          <i className={`text-2xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`} aria-hidden="true"></i>
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-4 md:hidden">
          <form onSubmit={onSearch} className="relative mb-3">
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜一搜萌系漫画"
              className="w-full rounded-full border border-background-200 bg-background-100 py-2.5 pl-4 pr-10 text-sm outline-none focus:border-primary-400"
            />
            <button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-foreground-500 cursor-pointer" aria-label="搜索">
              <i className="ri-search-line text-lg" aria-hidden="true"></i>
            </button>
          </form>
          <div className="flex flex-col gap-1">
            <Link to="/" onClick={() => setMenuOpen(false)} className="rounded-lg px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-secondary-100">
              首页
            </Link>
            {categories.map((c) => (
              <Link key={c.slug} to={`/category/${c.slug}`} onClick={() => setMenuOpen(false)} className="rounded-lg px-3 py-2.5 text-sm text-foreground-800 hover:bg-secondary-100">
                {c.name}
              </Link>
            ))}
            <Link to="/about" onClick={() => setMenuOpen(false)} className="rounded-lg px-3 py-2.5 text-sm text-foreground-800 hover:bg-secondary-100">
              关于我们
            </Link>
            <Link to="/faq" onClick={() => setMenuOpen(false)} className="rounded-lg px-3 py-2.5 text-sm text-foreground-800 hover:bg-secondary-100">
              常见问题
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}